import { useCallback, useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  TERMS_AND_CONDITIONS_INTRO,
  TERMS_AND_CONDITIONS_LAST_UPDATED,
  TERMS_AND_CONDITIONS_SECTIONS,
  TERMS_AND_CONDITIONS_TITLE,
} from "@/content/termsAndConditions"
import { getApiErrorMessage } from "@/lib/api"

const SCROLL_END_THRESHOLD_PX = 24

type TermsAndConditionsModalProps = {
  onAccept: () => Promise<boolean>
}

export function TermsAndConditionsModal({
  onAccept,
}: TermsAndConditionsModalProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false)
  const [hasReadTerms, setHasReadTerms] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const checkScrollPosition = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    const distanceFromEnd =
      el.scrollHeight - el.scrollTop - el.clientHeight
    if (distanceFromEnd <= SCROLL_END_THRESHOLD_PX) {
      setHasScrolledToEnd(true)
    }
  }, [])

  useEffect(() => {
    checkScrollPosition()
    const el = scrollRef.current
    if (!el) return

    const resizeObserver = new ResizeObserver(() => {
      checkScrollPosition()
    })
    resizeObserver.observe(el)
    window.addEventListener("resize", checkScrollPosition)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener("resize", checkScrollPosition)
    }
  }, [checkScrollPosition])

  async function handleAccept() {
    if (!hasScrolledToEnd || !hasReadTerms || isSubmitting) return
    setError(null)
    setIsSubmitting(true)
    try {
      const ok = await onAccept()
      if (!ok) {
        setError("Could not save your acceptance. Please try again.")
      }
    } catch (e) {
      setError(getApiErrorMessage(e))
    } finally {
      setIsSubmitting(false)
    }
  }

  const canAccept = hasScrolledToEnd && hasReadTerms

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        className="grid h-[min(90vh,720px)] max-h-[min(90vh,720px)] w-full max-w-[calc(100%-2rem)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:max-w-2xl"
        showCloseButton={false}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="shrink-0 border-b px-6 py-4 text-left">
          <DialogTitle>{TERMS_AND_CONDITIONS_TITLE}</DialogTitle>
          <DialogDescription>
            Last updated: {TERMS_AND_CONDITIONS_LAST_UPDATED}. Please scroll
            through the terms below, then accept to continue using Aegiss.
          </DialogDescription>
        </DialogHeader>

        <div
          ref={scrollRef}
          onScroll={checkScrollPosition}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4"
        >
          <div className="space-y-5 pr-2 text-sm leading-relaxed">
            <div className="space-y-3">
              {TERMS_AND_CONDITIONS_INTRO.map((paragraph, index) => (
                <p
                  key={paragraph}
                  className={
                    index === TERMS_AND_CONDITIONS_INTRO.length - 1
                      ? "font-medium text-foreground"
                      : "text-muted-foreground"
                  }
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {TERMS_AND_CONDITIONS_SECTIONS.map((section) => (
              <section key={section.heading} className="space-y-2">
                <h3 className="font-medium text-foreground">{section.heading}</h3>
                {section.paragraphs?.map((paragraph) => (
                  <p key={paragraph} className="text-muted-foreground">
                    {paragraph}
                  </p>
                ))}
                {section.bullets ? (
                  <ul className="list-disc space-y-1.5 pl-5 text-muted-foreground">
                    {section.bullets.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ))}
          </div>
        </div>

        <DialogFooter className="flex-col gap-3 border-t px-6 py-4 sm:flex-col sm:items-stretch">
          {!hasScrolledToEnd ? (
            <p className="text-center text-xs text-muted-foreground">
              Scroll to the bottom to enable acceptance
            </p>
          ) : null}
          <label
            className={`flex items-start gap-3 text-sm ${
              hasScrolledToEnd ? "cursor-pointer" : "cursor-not-allowed opacity-60"
            }`}
          >
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 accent-primary disabled:cursor-not-allowed"
              checked={hasReadTerms}
              disabled={!hasScrolledToEnd}
              onChange={(event) => setHasReadTerms(event.target.checked)}
            />
            <span>I have read and agree to the Terms of Service.</span>
          </label>
          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
          <Button
            type="button"
            className="w-full sm:w-auto sm:self-end"
            disabled={!canAccept || isSubmitting}
            onClick={() => void handleAccept()}
          >
            {isSubmitting ? "Saving..." : "I Agree"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
